import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { MapPin, Calendar, Truck, Zap, Camera, Sparkles, X, ShieldAlert, Clock, Wrench, Check, Eye, EyeOff, Plus, Trash2 } from 'lucide-react';
import { useAppAuth } from '../hooks/useAppAuth';
import { createApiClient, jobService, profileService, platformFeeConfigService, bazaarOrdersService } from '../services/api';
import type { UserProfileDto } from '../types';

// ─────────────────────────────────────────────
//  HELPERS
// ─────────────────────────────────────────────
function haversineMiles(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 3958.8;
  const toRad = (d: number) => d * Math.PI / 180;
  const dLat = toRad(lat2 - lat1), dLng = toRad(lng2 - lng1);
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

// ─────────────────────────────────────────────
//  LOCATION HELPERS
// ─────────────────────────────────────────────
type LocationSuggestion = {
  display: string;
  addressValue: string; // stored as the address field value
  lat?: number;
  lng?: number;
};

async function fetchLocationSuggestions(raw: string): Promise<LocationSuggestion[]> {
  const q = raw.trim();
  if (q.length < 2) return [];
  const isPostcodeLike = /^[A-Z]{1,2}\d/i.test(q);
  if (isPostcodeLike) {
    try {
      const r = await fetch(`https://api.postcodes.io/postcodes/${encodeURIComponent(q.replace(/\s/g, ''))}/autocomplete`);
      const d = await r.json();
      return ((d.result as string[]) ?? []).slice(0, 6).map(pc => ({ display: pc, addressValue: pc }));
    } catch { return []; }
  }
  // Place / town / city name
  try {
    const r = await fetch(`https://api.postcodes.io/places?q=${encodeURIComponent(q)}&limit=7`);
    const d = await r.json();
    return ((d.result as any[]) ?? []).map(p => ({
      display: `${p.name_1}${p.name_2 ? `, ${p.name_2}` : ''} (${p.outcode})`,
      addressValue: `${p.name_1}, ${p.outcode}`,
      lat: p.latitude,
      lng: p.longitude,
    }));
  } catch { return []; }
}

// Resolve any typed string (postcode, outcode, partial) to { address, lat, lng }.
// Returns null if nothing could be found.
async function geocodeAddress(raw: string): Promise<{ address: string; lat: number; lng: number } | null> {
  const cleaned = raw.trim().toUpperCase();
  if (!cleaned) return null;
  // Full postcode attempt
  try {
    const r = await fetch(`https://api.postcodes.io/postcodes/${encodeURIComponent(cleaned.replace(/\s/g, ''))}`);
    const d = await r.json();
    if (d.result?.latitude) {
      const area = d.result.admin_ward || d.result.admin_district || '';
      return {
        address: area ? `${area}, ${cleaned}` : cleaned,
        lat: d.result.latitude,
        lng: d.result.longitude,
      };
    }
  } catch {}
  // Outcode fallback (e.g. HA1, SW10)
  const outcode = cleaned.split(/\s/)[0];
  try {
    const r = await fetch(`https://api.postcodes.io/outcodes/${encodeURIComponent(outcode)}`);
    const d = await r.json();
    if (d.result?.latitude) {
      return { address: outcode, lat: d.result.latitude, lng: d.result.longitude };
    }
  } catch {}
  return null;
}

function DropAddressField({ value, onChange, placeholder = 'Town, Postcode or Address' }: {
  value: string;
  onChange: (address: string, city: string, postCode: string, lat: number, lng: number) => void;
  placeholder?: string;
}) {
  const [suggestions, setSuggestions] = useState<LocationSuggestion[]>([]);
  const [open, setOpen]               = useState(false);
  const [resolving, setResolving]     = useState(false);
  const wrapRef          = useRef<HTMLDivElement>(null);
  const geocodedValueRef = useRef('');
  const [inputValue, setInputValue]   = useState(value);

  useEffect(() => {
    const q = inputValue.trim();
    if (q.length < 2) { setSuggestions([]); setOpen(false); return; }
    const t = setTimeout(async () => {
      const list = await fetchLocationSuggestions(q).catch(() => [] as LocationSuggestion[]);
      setSuggestions(list);
      setOpen(list.length > 0);
    }, 350);
    return () => clearTimeout(t);
  }, [inputValue]);

  useEffect(() => {
    const h = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const resolve = async (raw: string) => {
    const result = await geocodeAddress(raw);
    if (result) {
      geocodedValueRef.current = result.address;
      setInputValue(result.address);
      const parts = raw.trim().split(/\s+/);
      const city = result.address.split(',')[0]?.trim() ?? '';
      const postCode = parts.length >= 2 ? parts.slice(-2).join(' ').toUpperCase() : parts[0]?.toUpperCase() ?? '';
      onChange(result.address, city, postCode, result.lat, result.lng);
    }
  };

  const select = async (s: LocationSuggestion) => {
    setOpen(false); setSuggestions([]);
    if (s.lat !== undefined && s.lng !== undefined) {
      geocodedValueRef.current = s.addressValue;
      setInputValue(s.addressValue);
      onChange(s.addressValue, s.addressValue.split(',')[0]?.trim() ?? '', '', s.lat, s.lng);
      return;
    }
    setResolving(true);
    await resolve(s.addressValue);
    setResolving(false);
  };

  const handleBlur = async () => {
    setOpen(false);
    if (!inputValue.trim() || inputValue === geocodedValueRef.current) return;
    setResolving(true);
    await resolve(inputValue);
    setResolving(false);
  };

  return (
    <div ref={wrapRef} className="relative">
      <label className="block text-xs font-medium text-gray-600 mb-0.5">Address</label>
      <div className="relative">
        <MapPin className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-400 pointer-events-none" />
        <input
          value={inputValue}
          onChange={e => { setInputValue(e.target.value); geocodedValueRef.current = ''; }}
          onFocus={() => { if (suggestions.length > 0) setOpen(true); }}
          onBlur={handleBlur}
          placeholder={placeholder}
          autoComplete="off"
          className="w-full border border-gray-300 rounded-md pl-7 pr-7 py-1.5 text-xs focus:ring-1 focus:ring-indigo-500"
        />
        {resolving && (
          <span className="absolute right-2 top-1/2 -translate-y-1/2 w-3 h-3 border-2 border-indigo-400 border-t-transparent rounded-full animate-spin" />
        )}
      </div>
      {open && (
        <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-40 overflow-y-auto">
          {suggestions.map(s => (
            <button
              key={s.display}
              type="button"
              onMouseDown={() => select(s)}
              className="flex items-center gap-2 w-full text-left px-2 py-1.5 text-xs text-gray-700 hover:bg-gray-50 transition-colors"
            >
              <MapPin className="w-3 h-3 text-gray-400 shrink-0" />
              <span className="flex-1 truncate">{s.display}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function PostcodeField({ label, iconEl, value, onChange, onGeocode, error }: {
  label: string;
  iconEl: React.ReactNode;
  value: string;
  onChange: (v: string) => void;
  onGeocode: (lat: number, lng: number) => void;
  error?: string;
}) {
  const [suggestions, setSuggestions] = useState<LocationSuggestion[]>([]);
  const [open, setOpen]               = useState(false);
  const [resolving, setResolving]     = useState(false);
  const wrapRef           = useRef<HTMLDivElement>(null);
  const geocodedValueRef  = useRef(''); // tracks the last value we successfully geocoded

  useEffect(() => {
    const q = value.trim();
    if (q.length < 2) { setSuggestions([]); setOpen(false); return; }
    const t = setTimeout(async () => {
      const list = await fetchLocationSuggestions(q).catch(() => [] as LocationSuggestion[]);
      setSuggestions(list);
      setOpen(list.length > 0);
    }, 350);
    return () => clearTimeout(t);
  }, [value]);

  useEffect(() => {
    const h = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const select = async (s: LocationSuggestion) => {
    setOpen(false);
    setSuggestions([]);
    // Place suggestion already has lat/lng from the places API
    if (s.lat !== undefined && s.lng !== undefined) {
      geocodedValueRef.current = s.addressValue;
      onChange(s.addressValue);
      onGeocode(s.lat, s.lng);
      return;
    }
    // Postcode suggestion — geocode to get lat/lng and enrich with town name
    setResolving(true);
    const result = await geocodeAddress(s.addressValue);
    setResolving(false);
    if (result) {
      geocodedValueRef.current = result.address;
      onChange(result.address);
      onGeocode(result.lat, result.lng);
    } else {
      onChange(s.addressValue);
    }
  };

  // Geocode whatever the user typed manually (fires when focus leaves the field)
  const handleBlur = async () => {
    setOpen(false);
    const q = value.trim();
    if (!q || q === geocodedValueRef.current) return;
    setResolving(true);
    const result = await geocodeAddress(q);
    setResolving(false);
    if (result) {
      geocodedValueRef.current = result.address;
      onChange(result.address);
      onGeocode(result.lat, result.lng);
    }
  };

  return (
    <div ref={wrapRef} className="relative">
      <label className="block text-sm font-medium text-gray-700 mb-1">{iconEl}{label}</label>
      <div className="relative">
        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
        <input
          value={value}
          onChange={e => { onChange(e.target.value); geocodedValueRef.current = ''; }}
          onFocus={() => { if (suggestions.length > 0) setOpen(true); }}
          onBlur={handleBlur}
          placeholder="Postcode, e.g. LS1 4AP"
          autoComplete="off"
          className="w-full border border-gray-300 rounded-lg pl-9 pr-8 py-2 text-sm focus:ring-2 focus:ring-blue-500"
        />
        {resolving && (
          <span className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
        )}
      </div>
      {open && (
        <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-52 overflow-y-auto">
          {suggestions.map(s => (
            <button
              key={s.display}
              type="button"
              onMouseDown={() => select(s)}
              className="flex items-center gap-2 w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
            >
              <MapPin className="w-3.5 h-3.5 text-gray-400 shrink-0" />
              <span className="flex-1 truncate">{s.display}</span>
              {s.lat !== undefined && <span className="text-xs text-gray-400 shrink-0">town</span>}
            </button>
          ))}
        </div>
      )}
      {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
    </div>
  );
}

// ─────────────────────────────────────────────
//  JOB TYPE SELECTOR
// ─────────────────────────────────────────────
function JobTypeSelector({ active }: { active: 'delivery' | 'repair' }) {
  const navigate = useNavigate();
  return (
    <div className="grid grid-cols-2 gap-3 mb-8">
      {/* Delivery card */}
      {active === 'delivery' ? (
        <div className="relative rounded-2xl border-2 border-blue-500 bg-gradient-to-br from-blue-50 to-indigo-50 p-5 shadow-sm">
          <div className="absolute top-3 right-3 w-5 h-5 rounded-full bg-blue-600 flex items-center justify-center">
            <Check className="w-3 h-3 text-white" strokeWidth={3} />
          </div>
          <div className="w-11 h-11 rounded-xl bg-blue-600 flex items-center justify-center mb-3 shadow">
            <Truck className="w-5 h-5 text-white" />
          </div>
          <p className="font-bold text-gray-900 text-base leading-tight">Delivery</p>
          <p className="text-xs text-gray-500 mt-1 leading-relaxed">Move goods from A to B — same day or scheduled.</p>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => navigate('/post-job')}
          className="group rounded-2xl border-2 border-gray-200 bg-white p-5 text-left hover:border-blue-300 hover:bg-blue-50/50 transition-all"
        >
          <div className="w-11 h-11 rounded-xl bg-gray-100 flex items-center justify-center mb-3 group-hover:bg-blue-100 transition-colors">
            <Truck className="w-5 h-5 text-gray-500 group-hover:text-blue-600 transition-colors" />
          </div>
          <p className="font-bold text-gray-700 text-base leading-tight group-hover:text-gray-900">Delivery</p>
          <p className="text-xs text-gray-400 mt-1 leading-relaxed">Move goods from A to B.</p>
        </button>
      )}

      {/* Repair card */}
      {active === 'repair' ? (
        <div className="relative rounded-2xl border-2 border-orange-400 bg-gradient-to-br from-orange-50 to-amber-50 p-5 shadow-sm">
          <div className="absolute top-3 right-3 w-5 h-5 rounded-full bg-orange-500 flex items-center justify-center">
            <Check className="w-3 h-3 text-white" strokeWidth={3} />
          </div>
          <div className="w-11 h-11 rounded-xl bg-orange-500 flex items-center justify-center mb-3 shadow">
            <Wrench className="w-5 h-5 text-white" />
          </div>
          <p className="font-bold text-gray-900 text-base leading-tight">Repair</p>
          <p className="text-xs text-gray-500 mt-1 leading-relaxed">Book a workshop for vehicle servicing or repairs.</p>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => navigate('/post-repair')}
          className="group rounded-2xl border-2 border-gray-200 bg-white p-5 text-left hover:border-orange-300 hover:bg-orange-50/50 transition-all"
        >
          <div className="w-11 h-11 rounded-xl bg-gray-100 flex items-center justify-center mb-3 group-hover:bg-orange-100 transition-colors">
            <Wrench className="w-5 h-5 text-gray-500 group-hover:text-orange-500 transition-colors" />
          </div>
          <p className="font-bold text-gray-700 text-base leading-tight group-hover:text-gray-900">Repair</p>
          <p className="text-xs text-gray-400 mt-1 leading-relaxed">Book a workshop for vehicle servicing or repairs.</p>
        </button>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
//  VALIDATION SCHEMA
// ─────────────────────────────────────────────
const postJobSchema = z.object({
  // Delivery timing UI fields (mapped to isSameDay/scheduledAt in onSubmit)
  deliveryType:    z.enum(['same-day', 'scheduled']),
  pickupTime:      z.string().optional(),
  deliveryTime:    z.string().optional(),
  schedulingType:  z.enum(['flexible', 'specific']),
  scheduledAt:     z.string().optional(),

  title:               z.string().min(5, 'Title must be at least 5 characters'),
  description:         z.string().min(10, 'Description must be at least 10 characters'),
  jobType:             z.number().int(),
  pickupAddress:       z.string().min(2, 'Pickup address is required'),
  deliveryAddress:     z.string().min(2, 'Delivery address is required'),
  estimatedValueGbp:   z.number().optional(),
  requiredVehicleType: z.number().int(),
  isRelayJob:          z.boolean(),
  pickupLat:           z.number().default(51.8787),
  pickupLng:           z.number().default(-0.4200),
  deliveryLat:         z.number().default(51.5074),
  deliveryLng:         z.number().default(-0.1278),
  // Freight details (all optional)
  // valueAsNumber:true leaves empty inputs as NaN; coerce NaN→undefined before Zod validates.
  weightKg:            z.preprocess(v => (typeof v === 'number' && isNaN(v) ? undefined : v), z.number().min(0).optional()),
  loadingMetresLdm:    z.preprocess(v => (typeof v === 'number' && isNaN(v) ? undefined : v), z.number().min(0).optional()),
  isStackable:         z.boolean().default(false),
  requiresTailLift:    z.boolean().default(false),
  requiresTwoMan:      z.boolean().default(false),
  requiresHiab:        z.boolean().default(false),
  // Address privacy
  isPickupAddressHidden:   z.boolean().default(false),
  isDeliveryAddressHidden: z.boolean().default(false),
});

type PostJobFormData = z.infer<typeof postJobSchema>;

// ─────────────────────────────────────────────
//  POST JOB PAGE
// ─────────────────────────────────────────────
export function PostJobPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isSubmitting, setIsSubmitting]     = useState(false);
  const [isSavingDraft, setIsSavingDraft]   = useState(false);
  const [error, setError]                   = useState<string | null>(null);
  const [goodsFiles, setGoodsFiles]         = useState<File[]>([]);
  const [goodsPreviews, setGoodsPreviews]   = useState<string[]>([]);
  const [isDragOver, setIsDragOver]         = useState(false);
  const [aiTitleGenerating, setAiTitleGenerating]   = useState(false);
  const [aiDescGenerating, setAiDescGenerating]     = useState(false);
  const [aiDescImproving, setAiDescImproving]       = useState(false);
  const [paymentTerm, setPaymentTerm]       = useState<0 | 1>(0); // 0=Cash, 1=Credit
  const [creditDays, setCreditDays]         = useState<number>(30);
  const [creditDaysCustom, setCreditDaysCustom] = useState('');
  const [isClosedBidding, setIsClosedBidding] = useState(false);
  const [isInternal, setIsInternal]         = useState(false);
  const [profile, setProfile]               = useState<UserProfileDto | null>(null);
  // Carriers only - the API refuses IsInternal from every other role, and the vehicle/driver
  // assignment panel a carrier needs afterwards is itself gated on the Carrier role.
  const canPostInternal = profile?.role === 'Carrier';
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { getToken, isAuthenticated, login } = useAppAuth();

  useEffect(() => {
    if (!isAuthenticated) { login(); return; }
    const client = createApiClient(getToken);
    profileService(client).getProfile().then(setProfile).catch(() => {});
  }, [isAuthenticated, login, getToken]);

  const { register, handleSubmit, watch, setValue, formState: { errors } } = useForm<PostJobFormData>({
    resolver: zodResolver(postJobSchema),
    defaultValues: {
      deliveryType:        'scheduled',
      schedulingType:      'specific',
      scheduledAt:         new Date(Date.now() + 86400000).toISOString().slice(0, 16),
      jobType:             1,
      requiredVehicleType: 0,
      isRelayJob:          false,
      pickupLat: 51.8787, pickupLng: -0.4200,
      deliveryLat: 51.5074, deliveryLng: -0.1278,
    },
  });

  // Deep-link prefill from Bazaar's "Get This Delivered" / "Post a Delivery Job" link —
  // used by both the seller (pickup only) and the buyer (pickup + delivery) of a
  // collection-only listing. The user reviews/edits these before posting.
  useEffect(() => {
    const pickupAddress   = searchParams.get('pickupAddress');
    const deliveryAddress = searchParams.get('deliveryAddress');
    const description     = searchParams.get('description');
    if (pickupAddress)   setValue('pickupAddress', pickupAddress);
    if (deliveryAddress) setValue('deliveryAddress', deliveryAddress);
    if (description)     setValue('description', description);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const deliveryType    = watch('deliveryType');
  const schedulingType  = watch('schedulingType');
  const pickupAddress   = watch('pickupAddress')   ?? '';
  const deliveryAddress = watch('deliveryAddress') ?? '';
  const titleValue      = watch('title')           ?? '';
  const descValue       = watch('description')     ?? '';
  const jobTypeValue    = watch('jobType');
  const isMultiDrop         = jobTypeValue === 6;
  const isMultiPickupDrop   = jobTypeValue === 7;
  const pickupHidden    = watch('isPickupAddressHidden')   ?? false;
  const deliveryHidden  = watch('isDeliveryAddressHidden') ?? false;

  const hasImage = goodsFiles.some(f => f.type.startsWith('image/'));

  const [pickupGeo,   setPickupGeo]   = useState<{ lat: number; lng: number } | null>(null);
  const [deliveryGeo, setDeliveryGeo] = useState<{ lat: number; lng: number } | null>(null);

  type DropEntry = {
    id: string;
    address: string; city: string; postCode: string;
    lat: number; lng: number;
    goodsDescription: string;
    recipientName: string; recipientPhone: string;
    specialInstructions: string;
    estimatedValueGbp: string;
  };
  const newDrop = (): DropEntry => ({
    id: crypto.randomUUID(),
    address: '', city: '', postCode: '',
    lat: 0, lng: 0,
    goodsDescription: '', recipientName: '', recipientPhone: '',
    specialInstructions: '', estimatedValueGbp: '',
  });
  const [drops, setDrops]     = useState<DropEntry[]>([newDrop(), newDrop()]);
  const [pickups, setPickups] = useState<DropEntry[]>([newDrop(), newDrop()]);
  const estimatedMiles = pickupGeo && deliveryGeo
    ? haversineMiles(pickupGeo.lat, pickupGeo.lng, deliveryGeo.lat, deliveryGeo.lng)
    : null;

  const ACCEPT = 'image/jpeg,image/png,image/webp,video/mp4,video/webm,video/quicktime';
  const MAX_FILES = 10;
  const MAX_BYTES = 15 * 1024 * 1024;

  const addFiles = (incoming: File[]) => {
    const valid = incoming.filter(f => f.size <= MAX_BYTES);
    setGoodsFiles(prev => {
      const merged = [...prev, ...valid].slice(0, MAX_FILES);
      setGoodsPreviews(prevP => {
        const newPreviews = valid.slice(0, MAX_FILES - prev.length).map(f => URL.createObjectURL(f));
        return [...prevP, ...newPreviews].slice(0, MAX_FILES);
      });
      return merged;
    });
  };

  const removeFile = (i: number) => {
    URL.revokeObjectURL(goodsPreviews[i]);
    setGoodsFiles(prev => prev.filter((_, idx) => idx !== i));
    setGoodsPreviews(prev => prev.filter((_, idx) => idx !== i));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    addFiles(Array.from(e.target.files ?? []));
    e.target.value = '';
  };

  const handleDragOver  = (e: React.DragEvent) => { e.preventDefault(); setIsDragOver(true); };
  const handleDragLeave = (e: React.DragEvent) => { e.preventDefault(); setIsDragOver(false); };
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    addFiles(Array.from(e.dataTransfer.files));
  };

  const toBase64 = (file: File): Promise<{ base64: string; mimeType: string }> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const dataUrl = reader.result as string;
        resolve({ base64: dataUrl.split(',')[1], mimeType: file.type || 'image/jpeg' });
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const handleAiGenerateTitle = async () => {
    const imageFile = goodsFiles.find(f => f.type.startsWith('image/'));
    if (!imageFile || titleValue.trim()) return;
    setAiTitleGenerating(true);
    try {
      const client = createApiClient(getToken);
      let description = descValue.trim();
      if (!description) {
        const { base64, mimeType } = await toBase64(imageFile);
        const { data } = await client.post<{ description: string }>('/api/jobs/describe-image', { base64Image: base64, mimeType });
        description = data.description;
      }
      const { data } = await client.post<{ title: string }>('/api/jobs/generate-title', { description });
      setValue('title', data.title, { shouldValidate: true });
    } catch {
      setError('AI could not generate a title. Please enter it manually.');
    } finally {
      setAiTitleGenerating(false);
    }
  };

  const handleAiGenerateDescription = async () => {
    const imageFile = goodsFiles.find(f => f.type.startsWith('image/'));
    if (!imageFile || descValue.trim()) return;
    setAiDescGenerating(true);
    try {
      const { base64, mimeType } = await toBase64(imageFile);
      const client = createApiClient(getToken);
      const { data } = await client.post<{ description: string }>('/api/jobs/describe-image', { base64Image: base64, mimeType });
      setValue('description', data.description, { shouldValidate: true });
    } catch {
      setError('AI could not analyse the image. Please fill in the description manually.');
    } finally {
      setAiDescGenerating(false);
    }
  };

  const handleAiImproveDescription = async () => {
    if (!descValue.trim()) return;
    setAiDescImproving(true);
    try {
      const client = createApiClient(getToken);
      const { data } = await client.post<{ description: string }>('/api/jobs/structure-description', { description: descValue });
      setValue('description', data.description, { shouldValidate: true });
    } catch {
      setError('AI could not improve the description. Please edit it manually.');
    } finally {
      setAiDescImproving(false);
    }
  };

  const onSubmit = async (data: PostJobFormData) => {
    if (paymentTerm === 1 && !isCreditEligible) {
      navigate('/profile?returnTo=/post-job&creditRequired=1');
      return;
    }
    setIsSubmitting(true);
    setError(null);
    try {
      const isSameDay = data.deliveryType === 'same-day';

      let scheduledAt: string;
      if (isSameDay) {
        const d = new Date();
        if (data.deliveryTime) {
          const [h, m] = data.deliveryTime.split(':').map(Number);
          d.setHours(h, m, 0, 0);
        }
        scheduledAt = d.toISOString();
      } else if (data.schedulingType === 'specific' && data.scheduledAt) {
        scheduledAt = new Date(data.scheduledAt).toISOString();
      } else {
        const d = new Date();
        d.setFullYear(d.getFullYear() + 1);
        scheduledAt = d.toISOString();
      }

      const jobType = isSameDay ? 0 : data.jobType === 0 ? 1 : data.jobType;

      const client = createApiClient(getToken);
      const response = await client.post('/api/jobs', {
        ...data,
        isSameDay,
        scheduledAt,
        jobType,
        goodsDescription: data.description,
        paymentTerm,
        creditDays: paymentTerm === 1 ? creditDays : undefined,
        isClosedBidding,
        isInternal,
        isPickupAddressHidden:   data.isPickupAddressHidden,
        isDeliveryAddressHidden: data.isDeliveryAddressHidden,
        drops: (jobType === 6 || jobType === 7) ? drops.map((d, i) => ({
          dropOrder:            i + 1,
          address:              d.address,
          city:                 d.city,
          postCode:             d.postCode,
          latitude:             d.lat,
          longitude:            d.lng,
          goodsDescription:     d.goodsDescription,
          recipientName:        d.recipientName,
          recipientPhone:       d.recipientPhone,
          specialInstructions:  d.specialInstructions,
          estimatedValueGbp:    d.estimatedValueGbp ? parseFloat(d.estimatedValueGbp) : undefined,
        })) : undefined,
        pickups: jobType === 7 ? pickups.map((p, i) => ({
          pickupOrder:          i + 1,
          address:              p.address,
          city:                 p.city,
          postCode:             p.postCode,
          latitude:             p.lat,
          longitude:            p.lng,
          goodsDescription:     p.goodsDescription,
          senderName:           p.recipientName,
          senderPhone:          p.recipientPhone,
          specialInstructions:  p.specialInstructions,
          estimatedValueGbp:    p.estimatedValueGbp ? parseFloat(p.estimatedValueGbp) : undefined,
        })) : undefined,
      });
      const jobId = response.data.jobId;

      // Deep-linked from a Bazaar order's checkout/confirmation — link the job we just created
      // back to that order item so the order shows it as arranged. Best-effort: the job is
      // posted either way, so a failed link here shouldn't block the normal "job created" flow.
      const bazaarOrderId     = searchParams.get('bazaarOrderId');
      const bazaarOrderItemId = searchParams.get('bazaarOrderItemId');
      if (bazaarOrderId && bazaarOrderItemId) {
        await bazaarOrdersService(client).linkDeliveryJob(bazaarOrderId, bazaarOrderItemId, jobId).catch(() => {});
      }

      if (goodsFiles.length > 0) {
        const svc = jobService(client);
        const images = await Promise.all(goodsFiles.map(toBase64));
        const uploaded = await svc.uploadImages(jobId, images).catch(() => null);
        if (!uploaded) {
          // Job was created but photo upload failed; navigate to the job with a warning flag.
          navigate(`/jobs/${jobId}?warning=photos`);
          return;
        }
      }

      navigate(`/jobs/${jobId}`);
    } catch (err: any) {
      setError(err.message ?? 'Failed to post job. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const onSaveDraft = async (data: PostJobFormData) => {
    setIsSavingDraft(true);
    setError(null);
    try {
      const isSameDay = data.deliveryType === 'same-day';
      let scheduledAt: string;
      if (isSameDay) {
        const d = new Date();
        if (data.deliveryTime) {
          const [h, m] = data.deliveryTime.split(':').map(Number);
          d.setHours(h, m, 0, 0);
        }
        scheduledAt = d.toISOString();
      } else if (data.schedulingType === 'specific' && data.scheduledAt) {
        scheduledAt = new Date(data.scheduledAt).toISOString();
      } else {
        const d = new Date();
        d.setFullYear(d.getFullYear() + 1);
        scheduledAt = d.toISOString();
      }
      const jobType = isSameDay ? 0 : data.jobType === 0 ? 1 : data.jobType;
      const client = createApiClient(getToken);
      const response = await client.post('/api/jobs', {
        ...data, isSameDay, scheduledAt, jobType,
        goodsDescription: data.description, paymentTerm,
        isPickupAddressHidden:   data.isPickupAddressHidden,
        isDeliveryAddressHidden: data.isDeliveryAddressHidden,
        drops: (jobType === 6 || jobType === 7) ? drops.map((d, i) => ({
          dropOrder: i + 1, address: d.address, city: d.city, postCode: d.postCode,
          latitude: d.lat, longitude: d.lng, goodsDescription: d.goodsDescription,
          recipientName: d.recipientName, recipientPhone: d.recipientPhone,
          specialInstructions: d.specialInstructions,
          estimatedValueGbp: d.estimatedValueGbp ? parseFloat(d.estimatedValueGbp) : undefined,
        })) : undefined,
        pickups: jobType === 7 ? pickups.map((p, i) => ({
          pickupOrder: i + 1, address: p.address, city: p.city, postCode: p.postCode,
          latitude: p.lat, longitude: p.lng, goodsDescription: p.goodsDescription,
          senderName: p.recipientName, senderPhone: p.recipientPhone,
          specialInstructions: p.specialInstructions,
          estimatedValueGbp: p.estimatedValueGbp ? parseFloat(p.estimatedValueGbp) : undefined,
        })) : undefined,
        saveAsDraft: true,
      });
      const jobId = response.data.jobId;
      if (goodsFiles.length > 0) {
        const images = await Promise.all(goodsFiles.map(toBase64));
        await jobService(client).uploadImages(jobId, images).catch(() => {});
      }
      navigate('/my-jobs');
    } catch (err: any) {
      setError(err.message ?? 'Failed to save draft.');
    } finally {
      setIsSavingDraft(false);
    }
  };

  const isCreditEligible = profile?.isCreditPaymentEligible === true;

  const handleSelectCredit = () => {
    if (!isCreditEligible) {
      navigate('/profile?returnTo=/post-job&creditRequired=1');
      return;
    }
    setPaymentTerm(1);
  };

  const cardBase    = 'flex-1 rounded-xl border-2 p-4 cursor-pointer transition-all text-left';
  const cardActive  = 'border-blue-500 bg-blue-50';
  const cardInactive = 'border-gray-200 bg-white hover:border-gray-300';
  const chipBase    = 'px-3 py-1.5 rounded-full border text-sm cursor-pointer transition-all';
  const chipActive  = 'border-blue-500 bg-blue-500 text-white';
  const chipInactive = 'border-gray-300 bg-white text-gray-700 hover:border-gray-400';

  const CREDIT_DAY_PRESETS = [7, 14, 30, 60];

  if (!isAuthenticated) return null;

  return (
    <div className="page-shell">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Post a Job</h1>
        <p className="mt-1 text-sm text-gray-500">Free to post. Choose the job type below.</p>
      </div>

      {/* ── Job type selector ────────────────────────────────────────── */}
      <JobTypeSelector active="delivery" />

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">

        {/* ── 1. When do you need it? ──────────────────────────────── */}
        <div>
          <p className="text-sm font-semibold text-gray-700 mb-2">When do you need it?</p>
          <div className="flex items-center gap-1 p-1 bg-gray-100 rounded-xl">
            <button
              type="button"
              aria-label="Same Day"
              onClick={() => setValue('deliveryType', 'same-day')}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-semibold transition-all ${
                deliveryType === 'same-day'
                  ? 'bg-white text-amber-700 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <Zap className="w-4 h-4" /> Same Day
            </button>
            <button
              type="button"
              aria-label="Scheduled"
              onClick={() => setValue('deliveryType', 'scheduled')}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-semibold transition-all ${
                deliveryType === 'scheduled'
                  ? 'bg-white text-blue-700 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <Calendar className="w-4 h-4" /> Scheduled
            </button>
          </div>
        </div>

        {deliveryType === 'same-day' && (
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 space-y-4">
            <p className="text-xs font-semibold text-amber-700 uppercase tracking-wide">Today's timings</p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Pickup Time</label>
                <input
                  type="time"
                  {...register('pickupTime')}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-amber-400"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Deliver By</label>
                <input
                  type="time"
                  {...register('deliveryTime')}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-amber-400"
                />
              </div>
            </div>
          </div>
        )}

        {deliveryType === 'scheduled' && (
          <div className="rounded-xl border border-blue-200 bg-blue-50 p-4 space-y-4">
            <p className="text-xs font-semibold text-blue-700 uppercase tracking-wide">Schedule</p>
            <div className="flex gap-2">
              <button
                type="button"
                className={`${chipBase} ${schedulingType === 'flexible' ? chipActive : chipInactive}`}
                onClick={() => setValue('schedulingType', 'flexible')}
              >
                Flexible window
              </button>
              <button
                type="button"
                className={`${chipBase} ${schedulingType === 'specific' ? chipActive : chipInactive}`}
                onClick={() => setValue('schedulingType', 'specific')}
              >
                Specific date &amp; time
              </button>
            </div>
            {schedulingType === 'flexible' && (
              <div className="rounded-lg bg-blue-100 px-4 py-3 text-sm text-blue-700">
                Timing will be agreed with the driver during bidding.
              </div>
            )}
            {schedulingType === 'specific' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Calendar className="inline w-3 h-3 mr-1" />
                  Date &amp; Time
                </label>
                <input
                  type="datetime-local"
                  {...register('scheduledAt')}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
                />
              </div>
            )}
          </div>
        )}

        {/* ── 2. Goods photos / videos ─────────────────────────────── */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            <Camera className="inline w-3 h-3 mr-1" />
            Goods Photos &amp; Videos{' '}
            <span className="text-gray-400 font-normal">(optional — up to {MAX_FILES} files, helps AI describe your goods)</span>
          </label>

          {goodsPreviews.length > 0 && (
            <div className="grid grid-cols-4 gap-2 mb-2">
              {goodsPreviews.map((src, i) => (
                <div key={i} className="relative rounded-lg overflow-hidden border border-gray-200 bg-gray-50" style={{ aspectRatio: '1' }}>
                  {goodsFiles[i]?.type.startsWith('video/') ? (
                    <video src={src} className="w-full h-full object-cover" muted playsInline />
                  ) : (
                    <img src={src} alt={`Goods ${i + 1}`} className="w-full h-full object-cover" />
                  )}
                  <button
                    type="button"
                    onClick={() => removeFile(i)}
                    className="absolute top-1 right-1 bg-black/60 hover:bg-black/80 text-white rounded-full p-0.5 transition-colors"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {goodsPreviews.length < MAX_FILES && (
            <div
              onDragOver={handleDragOver}
              onDragEnter={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-colors ${
                isDragOver ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-blue-400'
              }`}
            >
              <Camera className="w-7 h-7 text-gray-400 mx-auto mb-1.5" />
              <p className="text-sm text-gray-500">
                {goodsPreviews.length === 0
                  ? 'Click or drag & drop photos / videos here'
                  : `Add more (${goodsPreviews.length}/${MAX_FILES} added)`}
              </p>
              <p className="text-xs text-gray-400 mt-0.5">JPG, PNG, WebP, MP4, MOV — up to 15 MB each</p>
            </div>
          )}

          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPT}
            multiple
            className="hidden"
            onChange={handleFileChange}
          />
        </div>

        {/* ── 3/4. Pickup + Delivery Address ───────────────────────── */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="space-y-1">
            <PostcodeField
              label="Pickup Address"
              iconEl={<MapPin className="inline w-3 h-3 mr-1 text-green-600" />}
              value={pickupAddress}
              onChange={v => setValue('pickupAddress', v, { shouldValidate: true })}
              onGeocode={(lat, lng) => {
                setValue('pickupLat', lat);
                setValue('pickupLng', lng);
                setPickupGeo({ lat, lng });
              }}
              error={errors.pickupAddress?.message}
            />
            <button
              type="button"
              data-testid="pickup-privacy-toggle"
              onClick={() => setValue('isPickupAddressHidden', !pickupHidden)}
              className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border transition-colors ${
                pickupHidden
                  ? 'border-amber-400 bg-amber-50 text-amber-700'
                  : 'border-gray-300 bg-white text-gray-500 hover:border-gray-400'
              }`}
            >
              {pickupHidden ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
              {pickupHidden ? 'Pickup hidden from carriers (Town + Outcode only)' : 'Show full pickup address to carriers'}
            </button>
          </div>

          <div className="space-y-1">
            <PostcodeField
              label="Delivery Address"
              iconEl={<MapPin className="inline w-3 h-3 mr-1 text-red-600" />}
              value={deliveryAddress}
              onChange={v => setValue('deliveryAddress', v, { shouldValidate: true })}
              onGeocode={(lat, lng) => {
                setValue('deliveryLat', lat);
                setValue('deliveryLng', lng);
                setDeliveryGeo({ lat, lng });
              }}
              error={errors.deliveryAddress?.message}
            />
            <button
              type="button"
              onClick={() => setValue('isDeliveryAddressHidden', !deliveryHidden)}
              className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border transition-colors ${
                deliveryHidden
                  ? 'border-amber-400 bg-amber-50 text-amber-700'
                  : 'border-gray-300 bg-white text-gray-500 hover:border-gray-400'
              }`}
            >
              {deliveryHidden ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
              {deliveryHidden ? 'Delivery hidden from carriers (Town + Outcode only)' : 'Show full delivery address to carriers'}
            </button>
          </div>
        </div>
        {estimatedMiles !== null && (
          <div className="flex items-center gap-2 text-sm text-blue-700 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
            <MapPin className="w-4 h-4 shrink-0" />
            Estimated distance: <strong>{estimatedMiles.toFixed(1)} miles</strong>
          </div>
        )}

        {/* ── 5. Job Title ─────────────────────────────────────────── */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="text-sm font-medium text-gray-700">
              Job Title <span className="text-gray-400 font-normal text-xs">(required)</span>
            </label>
            {!titleValue.trim() && hasImage && (
              <button
                type="button"
                onClick={handleAiGenerateTitle}
                disabled={aiTitleGenerating}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border border-violet-400 bg-violet-50 text-violet-700 hover:bg-violet-100 text-xs font-semibold transition-colors"
              >
                {aiTitleGenerating
                  ? <span className="w-3 h-3 border-2 border-violet-400 border-t-transparent rounded-full animate-spin" />
                  : <Sparkles className="w-3 h-3" />
                }
                AI Generate
              </button>
            )}
          </div>
          <input
            {...register('title')}
            placeholder="e.g. Urgent document delivery Luton to London"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
          />
          {errors.title && <p className="mt-1 text-xs text-red-600">{errors.title.message}</p>}
        </div>

        {/* ── 6. Description with contextual AI buttons ────────────── */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label className="text-sm font-medium text-gray-700">Description</label>
            <div className="flex gap-2">
              {!descValue.trim() && hasImage && (
                <button
                  type="button"
                  onClick={handleAiGenerateDescription}
                  disabled={aiDescGenerating}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border border-violet-400 bg-violet-50 text-violet-700 hover:bg-violet-100 text-xs font-semibold transition-colors"
                >
                  {aiDescGenerating
                    ? <span className="w-3 h-3 border-2 border-violet-400 border-t-transparent rounded-full animate-spin" />
                    : <Sparkles className="w-3 h-3" />
                  }
                  AI Generate
                </button>
              )}
              {descValue.trim() && (
                <button
                  type="button"
                  onClick={handleAiImproveDescription}
                  disabled={aiDescImproving}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border border-emerald-400 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 text-xs font-semibold transition-colors"
                >
                  {aiDescImproving
                    ? <span className="w-3 h-3 border-2 border-emerald-400 border-t-transparent rounded-full animate-spin" />
                    : <Sparkles className="w-3 h-3" />
                  }
                  AI Improve
                </button>
              )}
            </div>
          </div>
          <textarea
            {...register('description')}
            rows={4}
            placeholder="Describe the goods, any special handling needed, and urgency..."
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
          />
          {errors.description && <p className="mt-1 text-xs text-red-600">{errors.description.message}</p>}
        </div>

        {/* ── 7. Vehicle + Service type ────────────────────────────── */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <Truck className="inline w-3 h-3 mr-1" />
              Vehicle Required <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <select
              {...register('requiredVehicleType', { valueAsNumber: true })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
            >
              <option value={0}>🏍️ Any (Motorcycle min)</option>
              <option value={1}>🚗 Car</option>
              <option value={2}>🚐 Small Van</option>
              <option value={3}>🚐 Midi Van</option>
              <option value={4}>🚚 LWB Van</option>
              <option value={5}>🚚 Luton Van</option>
              <option value={6}>🚛 7.5T</option>
              <option value={7}>🚛 12T</option>
              <option value={8}>🚛 18T</option>
              <option value={9}>🚛 26T</option>
              <option value={10}>🚛 Artic</option>
            </select>
          </div>
          <div>
            <label htmlFor="serviceType" className="block text-sm font-medium text-gray-700 mb-1">Service Type</label>
            <select
              id="serviceType"
              {...register('jobType', { valueAsNumber: true })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
            >
              <option value={1}>Standard Delivery</option>
              <option value={4}>Removal</option>
              <option value={5}>Freight / Haulage</option>
              <option value={6}>Multi Drop Delivery</option>
              <option value={7}>Multi Pickup Drop Delivery</option>
            </select>
          </div>
        </div>

        {/* ── 8. Multi Drop Stops ─────────────────────────────────── */}
        {isMultiDrop && (
          <div className="rounded-xl border border-indigo-200 bg-indigo-50 p-4 space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-sm font-semibold text-indigo-800">Drop Stops <span className="font-normal text-indigo-500 text-xs">({drops.length} of 10 max, min 2)</span></p>
              {drops.length < 10 && (
                <button
                  type="button"
                  onClick={() => setDrops(prev => [...prev, newDrop()])}
                  className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-indigo-600 text-white hover:bg-indigo-700 transition-colors"
                >
                  <Plus className="w-3 h-3" /> Add Drop
                </button>
              )}
            </div>

            {drops.map((drop, idx) => (
              <div key={drop.id} className="rounded-lg bg-white border border-indigo-200 p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-indigo-700">Stop {idx + 1}</span>
                  {drops.length > 2 && (
                    <button
                      type="button"
                      aria-label="Remove stop"
                      onClick={() => setDrops(prev => prev.filter(d => d.id !== drop.id))}
                      className="text-red-400 hover:text-red-600 transition-colors"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>

                <DropAddressField
                  value={drop.address}
                  placeholder="Drop stop postcode or address"
                  onChange={(address, city, postCode, lat, lng) =>
                    setDrops(prev => prev.map(d => d.id === drop.id ? { ...d, address, city, postCode, lat, lng } : d))
                  }
                />

                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-0.5">Recipient Name</label>
                    <input
                      value={drop.recipientName}
                      onChange={e => setDrops(prev => prev.map(d => d.id === drop.id ? { ...d, recipientName: e.target.value } : d))}
                      placeholder="John Smith"
                      className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-xs focus:ring-1 focus:ring-indigo-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-0.5">Recipient Phone</label>
                    <input
                      value={drop.recipientPhone}
                      onChange={e => setDrops(prev => prev.map(d => d.id === drop.id ? { ...d, recipientPhone: e.target.value } : d))}
                      placeholder="+44 7700 000000"
                      className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-xs focus:ring-1 focus:ring-indigo-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-0.5">Goods at this stop</label>
                  <input
                    value={drop.goodsDescription}
                    onChange={e => setDrops(prev => prev.map(d => d.id === drop.id ? { ...d, goodsDescription: e.target.value } : d))}
                    placeholder="e.g. 3 pallets of electronics"
                    className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-xs focus:ring-1 focus:ring-indigo-500"
                  />
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-0.5">Estimated Value (£)</label>
                    <input
                      type="number"
                      min={0}
                      step={0.01}
                      value={drop.estimatedValueGbp}
                      onChange={e => setDrops(prev => prev.map(d => d.id === drop.id ? { ...d, estimatedValueGbp: e.target.value } : d))}
                      placeholder="Optional"
                      className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-xs focus:ring-1 focus:ring-indigo-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-0.5">Special Instructions</label>
                    <input
                      value={drop.specialInstructions}
                      onChange={e => setDrops(prev => prev.map(d => d.id === drop.id ? { ...d, specialInstructions: e.target.value } : d))}
                      placeholder="e.g. Call before arrival"
                      className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-xs focus:ring-1 focus:ring-indigo-500"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ── 8b. Multi Pickup Drop — Pickup Stops ──────────────── */}
        {isMultiPickupDrop && (
          <div className="rounded-xl border border-violet-200 bg-violet-50 p-4 space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-sm font-semibold text-violet-800">Pickup Stops <span className="font-normal text-violet-500 text-xs">({pickups.length} of 10 max, min 2)</span></p>
              {pickups.length < 10 && (
                <button
                  type="button"
                  onClick={() => setPickups(prev => [...prev, newDrop()])}
                  className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-violet-600 text-white hover:bg-violet-700 transition-colors"
                >
                  <Plus className="w-3 h-3" /> Add Pickup
                </button>
              )}
            </div>

            {pickups.map((pickup, idx) => (
              <div key={pickup.id} className="rounded-lg bg-white border border-violet-200 p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-violet-700">Pickup {idx + 1}</span>
                  {pickups.length > 2 && (
                    <button
                      type="button"
                      aria-label="Remove pickup"
                      onClick={() => setPickups(prev => prev.filter(p => p.id !== pickup.id))}
                      className="text-red-400 hover:text-red-600 transition-colors"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>

                <DropAddressField
                  value={pickup.address}
                  placeholder="Pickup stop postcode or address"
                  onChange={(address, city, postCode, lat, lng) =>
                    setPickups(prev => prev.map(p => p.id === pickup.id ? { ...p, address, city, postCode, lat, lng } : p))
                  }
                />

                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-0.5">Sender Name</label>
                    <input
                      value={pickup.recipientName}
                      onChange={e => setPickups(prev => prev.map(p => p.id === pickup.id ? { ...p, recipientName: e.target.value } : p))}
                      placeholder="Jane Smith"
                      className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-xs focus:ring-1 focus:ring-violet-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-0.5">Sender Phone</label>
                    <input
                      value={pickup.recipientPhone}
                      onChange={e => setPickups(prev => prev.map(p => p.id === pickup.id ? { ...p, recipientPhone: e.target.value } : p))}
                      placeholder="+44 7700 000000"
                      className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-xs focus:ring-1 focus:ring-violet-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-0.5">Goods at this stop</label>
                  <input
                    value={pickup.goodsDescription}
                    onChange={e => setPickups(prev => prev.map(p => p.id === pickup.id ? { ...p, goodsDescription: e.target.value } : p))}
                    placeholder="e.g. 2 boxes of furniture"
                    className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-xs focus:ring-1 focus:ring-violet-500"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-0.5">Special Instructions</label>
                  <input
                    value={pickup.specialInstructions}
                    onChange={e => setPickups(prev => prev.map(p => p.id === pickup.id ? { ...p, specialInstructions: e.target.value } : p))}
                    placeholder="e.g. Call before arrival"
                    className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-xs focus:ring-1 focus:ring-violet-500"
                  />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ── 8c. Multi Pickup Drop — Drop Stops ──────────────────── */}
        {isMultiPickupDrop && (
          <div className="rounded-xl border border-indigo-200 bg-indigo-50 p-4 space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-sm font-semibold text-indigo-800">Drop Stops <span className="font-normal text-indigo-500 text-xs">({drops.length} of 10 max, min 2)</span></p>
              {drops.length < 10 && (
                <button
                  type="button"
                  onClick={() => setDrops(prev => [...prev, newDrop()])}
                  className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-indigo-600 text-white hover:bg-indigo-700 transition-colors"
                >
                  <Plus className="w-3 h-3" /> Add Drop
                </button>
              )}
            </div>

            {drops.map((drop, idx) => (
              <div key={drop.id} className="rounded-lg bg-white border border-indigo-200 p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-indigo-700">Drop {idx + 1}</span>
                  {drops.length > 2 && (
                    <button
                      type="button"
                      aria-label="Remove drop"
                      onClick={() => setDrops(prev => prev.filter(d => d.id !== drop.id))}
                      className="text-red-400 hover:text-red-600 transition-colors"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>

                <DropAddressField
                  value={drop.address}
                  placeholder="Drop stop postcode or address"
                  onChange={(address, city, postCode, lat, lng) =>
                    setDrops(prev => prev.map(d => d.id === drop.id ? { ...d, address, city, postCode, lat, lng } : d))
                  }
                />

                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-0.5">Recipient Name</label>
                    <input
                      value={drop.recipientName}
                      onChange={e => setDrops(prev => prev.map(d => d.id === drop.id ? { ...d, recipientName: e.target.value } : d))}
                      placeholder="John Smith"
                      className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-xs focus:ring-1 focus:ring-indigo-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-0.5">Recipient Phone</label>
                    <input
                      value={drop.recipientPhone}
                      onChange={e => setDrops(prev => prev.map(d => d.id === drop.id ? { ...d, recipientPhone: e.target.value } : d))}
                      placeholder="+44 7700 000000"
                      className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-xs focus:ring-1 focus:ring-indigo-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-0.5">Goods at this stop</label>
                  <input
                    value={drop.goodsDescription}
                    onChange={e => setDrops(prev => prev.map(d => d.id === drop.id ? { ...d, goodsDescription: e.target.value } : d))}
                    placeholder="e.g. 3 pallets of electronics"
                    className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-xs focus:ring-1 focus:ring-indigo-500"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-0.5">Special Instructions</label>
                  <input
                    value={drop.specialInstructions}
                    onChange={e => setDrops(prev => prev.map(d => d.id === drop.id ? { ...d, specialInstructions: e.target.value } : d))}
                    placeholder="e.g. Call before arrival"
                    className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-xs focus:ring-1 focus:ring-indigo-500"
                  />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ── 9. Freight Details ──────────────────────────────────── */}
        <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 space-y-4">
          <p className="text-sm font-semibold text-gray-700">Freight Details <span className="font-normal text-gray-400">(optional)</span></p>

          {/* Weight + LDM */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Weight (kg)</label>
              <input
                type="number"
                min={0}
                step={0.1}
                placeholder="e.g. 500"
                {...register('weightKg', { valueAsNumber: true })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 bg-white"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Loading Metres (LDM)</label>
              <input
                type="number"
                min={0}
                step={0.1}
                placeholder="e.g. 2.4"
                {...register('loadingMetresLdm', { valueAsNumber: true })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 bg-white"
              />
            </div>
          </div>

          {/* Requirement checkboxes */}
          <div className="flex flex-wrap gap-3">
            {([
              { field: 'requiresTwoMan'   as const, label: '👥 Two-man crew'      },
              { field: 'requiresTailLift' as const, label: '🔧 Tail lift'         },
              { field: 'requiresHiab'     as const, label: '🏗️ Hiab'             },
              { field: 'isStackable'      as const, label: '📦 Stackable goods'   },
            ] as const).map(({ field, label }) => {
              const checked = watch(field) ?? false;
              return (
                <button
                  key={field}
                  type="button"
                  onClick={() => setValue(field, !checked)}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-medium transition-colors ${
                    checked
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white text-gray-600 border-gray-300 hover:border-blue-300'
                  }`}
                >
                  {label}
                </button>
              );
            })}
          </div>
        </div>

        {/* ── 8b. Internal fleet job — carriers only ──────────────── */}
        {canPostInternal && (
          <div className="rounded-xl border border-gray-200 bg-white p-4" data-testid="internal-job-card">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <p className="text-sm font-semibold text-gray-700 flex items-center gap-1.5">
                  <Truck className="w-4 h-4 text-emerald-600" /> Internal Fleet Job
                </p>
                <p className="text-xs text-gray-500 mt-0.5">
                  {isInternal
                    ? 'Off-market. No other carrier can see or bid on this job — assign it straight to your own driver and vehicle.'
                    : 'Moving this load with your own driver? Keep it off the job board entirely.'}
                </p>
              </div>
              <button
                type="button"
                role="switch"
                data-testid="internal-job-toggle"
                aria-label="Internal Fleet Job"
                aria-checked={isInternal}
                onClick={() => { setIsInternal(v => !v); setIsClosedBidding(false); }}
                className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 ${
                  isInternal ? 'bg-emerald-600' : 'bg-gray-200'
                }`}
              >
                <span
                  className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                    isInternal ? 'translate-x-5' : 'translate-x-0'
                  }`}
                />
              </button>
            </div>
            {isInternal && (
              <div
                data-testid="internal-job-notice"
                className="mt-3 rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2 text-xs text-emerald-800"
              >
                This job goes straight to your fleet — no quotes, no platform commission and no
                escrow payment. You will assign a driver and vehicle on the job page, and they get
                GPS tracking, proof of collection and proof of delivery as normal.
              </div>
            )}
          </div>
        )}

        {/* Payment and bidding only exist because a job is bought from somebody else.
            An internal job has neither a counterparty nor a price. */}
        {!isInternal && (<>

        {/* ── 9. Payment Type ─────────────────────────────────────── */}
        <div className="rounded-xl border border-gray-200 bg-white p-4 space-y-3">
          <label className="block text-sm font-semibold text-gray-700">Payment Type</label>
          <div className="grid gap-3 grid-cols-2">
            <button
              type="button"
              aria-label="Cash"
              onClick={() => setPaymentTerm(0)}
              className={`text-left rounded-lg border-2 p-3 text-sm transition-colors ${
                paymentTerm === 0 ? 'border-blue-500 bg-blue-50' : 'border-gray-200 bg-white hover:border-gray-300'
              }`}
            >
              <span className="font-medium text-gray-900">Cash</span>
              <p className="mt-0.5 text-xs text-gray-500">Pay the full amount via card before the driver starts.</p>
            </button>

            <button
              type="button"
              aria-label="Credit"
              onClick={handleSelectCredit}
              className={`text-left rounded-lg border-2 p-3 text-sm transition-colors relative ${
                paymentTerm === 1 ? 'border-blue-500 bg-blue-50' : 'border-gray-200 bg-white hover:border-gray-300'
              } ${!isCreditEligible ? 'opacity-70' : ''}`}
            >
              <span className="font-medium text-gray-900">Credit</span>
              {!isCreditEligible && (
                <ShieldAlert className="w-3.5 h-3.5 text-amber-500 absolute top-2 right-2" />
              )}
              <p className="mt-0.5 text-xs text-gray-500">Settle after delivery — requires approved Credit account.</p>
              {!isCreditEligible && (
                <p className="mt-1 text-xs font-medium text-amber-600">Request Credit in your profile to unlock</p>
              )}
            </button>
          </div>

          {!isCreditEligible && paymentTerm === 1 && (
            <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2.5 text-sm text-amber-800">
              <ShieldAlert className="w-4 h-4 shrink-0 mt-0.5" />
              <span>
                Credit payment requires Admin approval.{' '}
                <button
                  type="button"
                  onClick={() => navigate('/profile?returnTo=/post-job&creditRequired=1')}
                  className="underline font-medium"
                >
                  Request a Credit account
                </button>
                {' '}in your profile.
              </span>
            </div>
          )}

          {paymentTerm === 1 && (
            <div className="pt-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">Credit Days</label>
              <div className="flex flex-wrap gap-2 mb-2">
                {CREDIT_DAY_PRESETS.map(d => (
                  <button
                    key={d}
                    type="button"
                    onClick={() => { setCreditDays(d); setCreditDaysCustom(''); }}
                    className={`${chipBase} ${creditDays === d && !creditDaysCustom ? chipActive : chipInactive}`}
                  >
                    {d} days
                  </button>
                ))}
                <div className="flex items-center gap-1.5">
                  <input
                    type="number"
                    min={1}
                    max={365}
                    placeholder="Custom"
                    value={creditDaysCustom}
                    onChange={e => {
                      setCreditDaysCustom(e.target.value);
                      const n = parseInt(e.target.value, 10);
                      if (!isNaN(n) && n > 0) setCreditDays(n);
                    }}
                    className="w-20 border border-gray-300 rounded-full px-3 py-1.5 text-sm focus:ring-2 focus:ring-blue-500 text-center"
                  />
                  {creditDaysCustom && <span className="text-xs text-gray-500">days</span>}
                </div>
              </div>
              <p className="text-xs text-gray-400">Payment due {creditDays} days after job completion.</p>
            </div>
          )}
        </div>

        {/* ── 10. Bidding type ────────────────────────────────────── */}
        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <p className="text-sm font-semibold text-gray-700">Bidding Type</p>
              <p className="text-xs text-gray-500 mt-0.5">
                {isClosedBidding
                  ? 'Closed — carrier bid prices are hidden from each other. Only you see all quotes.'
                  : 'Open — carriers can see each other\'s bid prices.'}
              </p>
            </div>
            <button
              type="button"
              role="switch"
              data-testid="closed-bidding-toggle"
              aria-label="Closed Bidding"
              aria-checked={isClosedBidding}
              onClick={() => setIsClosedBidding(v => !v)}
              className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-violet-500 focus:ring-offset-2 ${
                isClosedBidding ? 'bg-violet-600' : 'bg-gray-200'
              }`}
            >
              <span
                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                  isClosedBidding ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </button>
          </div>
          {isClosedBidding && (
            <div className="mt-3 rounded-lg bg-violet-50 border border-violet-200 px-3 py-2 text-xs text-violet-700">
              Carriers will only see their own bid price. You see all bids to select the best quote.
            </div>
          )}
        </div>

        </>)}

        {/* ── 11. Relay — only shown when distance > 150 miles ─────── */}
        {estimatedMiles !== null && estimatedMiles > 150 && (
          <div className="flex items-start gap-3 rounded-xl border border-orange-200 bg-orange-50 p-4">
            <input
              type="checkbox"
              id="relay"
              {...register('isRelayJob')}
              className="mt-0.5 w-4 h-4 text-orange-600 rounded"
            />
            <label htmlFor="relay" className="text-sm text-gray-700">
              <span className="font-semibold text-orange-700">Relay delivery</span>
              {' — '}split across multiple drivers for this long-distance route ({estimatedMiles.toFixed(0)} miles)
            </label>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">{error}</div>
        )}

        <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 text-xs text-blue-700">
          <strong>Free to post.</strong> All jobs are manually reviewed within 2 hours before going live.
          You'll be notified when your job is approved and when bids arrive.
          5% cashback on your first 3 completed orders.
        </div>

        <div style={{ display: 'flex', gap: 10 }}>
          <button
            type="button"
            onClick={handleSubmit(onSaveDraft)}
            disabled={isSavingDraft || isSubmitting}
            className="flex-1 bg-gray-100 hover:bg-gray-200 disabled:bg-gray-100 text-gray-700 font-medium py-3 px-6 rounded-lg transition-colors border border-gray-300"
          >
            {isSavingDraft ? 'Saving...' : 'Save as Draft'}
          </button>
          <button
            type="submit"
            aria-label="Post Job"
            disabled={isSubmitting || isSavingDraft}
            className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-medium py-3 px-6 rounded-lg transition-colors"
          >
            {isSubmitting ? 'Posting...' : 'Post Job — Free'}
          </button>
        </div>
      </form>
    </div>
  );
}

// ─────────────────────────────────────────────
//  POST REPAIR JOB PAGE
// ─────────────────────────────────────────────
const REPAIR_SERVICE_TYPES = [
  { value: 'mot',             label: 'MOT' },
  { value: 'annual_service',  label: 'Annual Service / Inspection' },
  { value: 'servicing',       label: 'Servicing' },
  { value: 'repairs',         label: 'General Repairs' },
  { value: 'tyres',           label: 'Tyres' },
  { value: 'diagnostics',     label: 'Diagnostics' },
  { value: 'bodywork',        label: 'Body Works' },
  { value: 'paintwork',       label: 'Paint Works' },
  { value: 'recovery',        label: 'Recovery' },
  { value: 'mobile_assist',   label: 'Mobile Roadside Assistance' },
] as const;

export type RepairServiceType = typeof REPAIR_SERVICE_TYPES[number]['value'];

const postRepairSchema = z.object({
  vehicleRegistration: z.string().min(2, 'Vehicle registration is required'),
  repairServiceType:   z.string().optional(),
  issueDescription:    z.string().min(10, 'Please describe the issue in detail'),
  locationAddress:     z.string().min(3, 'Vehicle location is required'),
  urgencyType:         z.enum(['urgent', 'scheduled', 'flexible']),
  urgentDeadline:      z.string().optional(),
  scheduledAt:         z.string().optional(),
  pickupLat:           z.number().default(51.5074),
  pickupLng:           z.number().default(-0.1278),
});

type PostRepairFormData = z.infer<typeof postRepairSchema>;

export function PostRepairJobPage() {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting]         = useState(false);
  const [isSavingDraft, setIsSavingDraft]       = useState(false);
  const [error, setError]                       = useState<string | null>(null);
  const [isClosedBidding, setIsClosedBidding]   = useState(false);
  const [vehicleFiles, setVehicleFiles]         = useState<File[]>([]);
  const [vehiclePreviews, setVehiclePreviews]   = useState<string[]>([]);
  const [isDragOver, setIsDragOver]             = useState(false);
  const [selectedServiceTypes, setSelectedServiceTypes] = useState<string[]>([]);
  const [aiDescGenerating, setAiDescGenerating]         = useState(false);
  const [aiDescImproving, setAiDescImproving]           = useState(false);
  const [aiServiceTyping, setAiServiceTyping]           = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { getToken, isAuthenticated, login } = useAppAuth();
  const [repairCommissionPercent, setRepairCommissionPercent] = useState<number | null>(null);

  useEffect(() => {
    if (!isAuthenticated) login();
  }, [isAuthenticated, login]);

  useEffect(() => {
    platformFeeConfigService(createApiClient(getToken)).getEffective()
      .then(cfg => setRepairCommissionPercent(cfg.repairJobCommissionPercent))
      .catch(() => {});
  }, [getToken]);

  const { register, handleSubmit, watch, setValue, formState: { errors } } = useForm<PostRepairFormData>({
    resolver: zodResolver(postRepairSchema),
    defaultValues: {
      urgencyType:     'scheduled',
      urgentDeadline:  new Date(Date.now() + 7_200_000).toISOString().slice(0, 16),
      scheduledAt:     new Date(Date.now() + 86_400_000).toISOString().slice(0, 16),
      pickupLat: 51.5074,
      pickupLng: -0.1278,
    },
  });

  const urgencyType     = watch('urgencyType');
  const locationAddress = watch('locationAddress') ?? '';
  const descValue       = watch('issueDescription') ?? '';
  const hasImage        = vehicleFiles.some(f => f.type.startsWith('image/'));

  const ACCEPT    = 'image/jpeg,image/png,image/webp,video/mp4,video/webm,video/quicktime';
  const MAX_FILES = 10;
  const MAX_BYTES = 15 * 1024 * 1024;

  const addFiles = (incoming: File[]) => {
    const valid = incoming.filter(f => f.size <= MAX_BYTES);
    setVehicleFiles(prev => {
      const merged = [...prev, ...valid].slice(0, MAX_FILES);
      setVehiclePreviews(prevP => {
        const newPreviews = valid.slice(0, MAX_FILES - prev.length).map(f => URL.createObjectURL(f));
        return [...prevP, ...newPreviews].slice(0, MAX_FILES);
      });
      return merged;
    });
  };

  const removeFile = (i: number) => {
    URL.revokeObjectURL(vehiclePreviews[i]);
    setVehicleFiles(prev => prev.filter((_, idx) => idx !== i));
    setVehiclePreviews(prev => prev.filter((_, idx) => idx !== i));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    addFiles(Array.from(e.target.files ?? []));
    e.target.value = '';
  };

  const handleDragOver  = (e: React.DragEvent) => { e.preventDefault(); setIsDragOver(true); };
  const handleDragLeave = (e: React.DragEvent) => { e.preventDefault(); setIsDragOver(false); };
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    addFiles(Array.from(e.dataTransfer.files));
  };

  const toBase64 = (file: File): Promise<{ base64: string; mimeType: string }> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const dataUrl = reader.result as string;
        resolve({ base64: dataUrl.split(',')[1], mimeType: file.type || 'image/jpeg' });
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const handleAiGenerateDescription = async () => {
    const imageFile = vehicleFiles.find(f => f.type.startsWith('image/'));
    if (!imageFile || descValue.trim()) return;
    setAiDescGenerating(true);
    try {
      const { base64, mimeType } = await toBase64(imageFile);
      const client = createApiClient(getToken);
      const { data } = await client.post<{ description: string }>('/api/jobs/describe-image', { base64Image: base64, mimeType });
      setValue('issueDescription', data.description, { shouldValidate: true });
    } catch {
      setError('AI could not analyse the image. Please fill in the description manually.');
    } finally {
      setAiDescGenerating(false);
    }
  };

  const handleAiImproveDescription = async () => {
    if (!descValue.trim()) return;
    setAiDescImproving(true);
    try {
      const client = createApiClient(getToken);
      const { data } = await client.post<{ description: string }>('/api/jobs/structure-description', { description: descValue });
      setValue('issueDescription', data.description, { shouldValidate: true });
    } catch {
      setError('AI could not improve the description. Please edit it manually.');
    } finally {
      setAiDescImproving(false);
    }
  };

  const handleAiDetectServiceType = async () => {
    if (!descValue.trim()) return;
    setAiServiceTyping(true);
    try {
      const client = createApiClient(getToken);
      const { data } = await client.post<{ serviceType: string }>(
        '/api/jobs/detect-service-type',
        { description: descValue }
      );
      if (data.serviceType && REPAIR_SERVICE_TYPES.some(s => s.value === data.serviceType)) {
        setSelectedServiceTypes(prev =>
          prev.includes(data.serviceType) ? prev : [...prev, data.serviceType]
        );
      }
    } catch {
      // silently ignore — user can select manually
    } finally {
      setAiServiceTyping(false);
    }
  };

  const onSubmit = async (data: PostRepairFormData) => {
    setIsSubmitting(true);
    setError(null);
    try {
      const isSameDay = data.urgencyType === 'urgent';
      let scheduledAt: string;
      if (data.urgencyType === 'urgent') {
        scheduledAt = data.urgentDeadline
          ? new Date(data.urgentDeadline).toISOString()
          : new Date(Date.now() + 7_200_000).toISOString();
      } else if (data.urgencyType === 'scheduled' && data.scheduledAt) {
        scheduledAt = new Date(data.scheduledAt).toISOString();
      } else {
        const d = new Date();
        d.setFullYear(d.getFullYear() + 1);
        scheduledAt = d.toISOString();
      }

      const client = createApiClient(getToken);
      const response = await client.post('/api/jobs', {
        jobType:             3,  // VehicleRepair
        title:               `Vehicle repair — ${data.vehicleRegistration.toUpperCase()}`,
        description:         data.issueDescription,
        goodsDescription:    `[${selectedServiceTypes.length > 0 ? selectedServiceTypes.join(', ') : 'repairs'}] Reg: ${data.vehicleRegistration.toUpperCase()} — ${data.issueDescription}`,
        repairServiceType:   selectedServiceTypes[0] ?? undefined,
        repairServiceTypes:  selectedServiceTypes.length > 0 ? selectedServiceTypes : undefined,
        isSameDay,
        isRelayJob:          false,
        isClosedBidding,
        scheduledAt,
        pickupAddress:       data.locationAddress,
        pickupLat:           data.pickupLat,
        pickupLng:           data.pickupLng,
        deliveryAddress:     data.locationAddress,
        deliveryLat:         data.pickupLat,
        deliveryLng:         data.pickupLng,
        requiredVehicleType: 0,
      });
      const jobId = response.data.jobId;

      if (vehicleFiles.length > 0) {
        const svc = jobService(client);
        const images = await Promise.all(vehicleFiles.map(toBase64));
        await svc.uploadImages(jobId, images).catch(() => {});
      }

      navigate(`/jobs/${jobId}`);
    } catch (err: any) {
      setError(err.message ?? 'Failed to post repair job. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const onSaveRepairDraft = async (data: PostRepairFormData) => {
    setIsSavingDraft(true);
    setError(null);
    try {
      const isSameDay = data.urgencyType === 'urgent';
      let scheduledAt: string;
      if (data.urgencyType === 'urgent') {
        scheduledAt = data.urgentDeadline
          ? new Date(data.urgentDeadline).toISOString()
          : new Date(Date.now() + 7_200_000).toISOString();
      } else if (data.urgencyType === 'scheduled' && data.scheduledAt) {
        scheduledAt = new Date(data.scheduledAt).toISOString();
      } else {
        const d = new Date(); d.setFullYear(d.getFullYear() + 1);
        scheduledAt = d.toISOString();
      }
      const client = createApiClient(getToken);
      const response = await client.post('/api/jobs', {
        jobType:            3,
        title:              `Vehicle repair — ${data.vehicleRegistration.toUpperCase()}`,
        description:        data.issueDescription,
        goodsDescription:   `[${selectedServiceTypes.length > 0 ? selectedServiceTypes.join(', ') : 'repairs'}] Reg: ${data.vehicleRegistration.toUpperCase()} — ${data.issueDescription}`,
        repairServiceTypes: selectedServiceTypes.length > 0 ? selectedServiceTypes : undefined,
        isSameDay, isRelayJob: false, isClosedBidding, scheduledAt,
        pickupAddress: data.locationAddress, pickupLat: data.pickupLat, pickupLng: data.pickupLng,
        deliveryAddress: data.locationAddress, deliveryLat: data.pickupLat, deliveryLng: data.pickupLng,
        requiredVehicleType: 0, saveAsDraft: true,
      });
      const jobId = response.data.jobId;
      if (vehicleFiles.length > 0) {
        const images = await Promise.all(vehicleFiles.map(toBase64));
        await jobService(client).uploadImages(jobId, images).catch(() => {});
      }
      navigate('/my-jobs');
    } catch (err: any) {
      setError(err.message ?? 'Failed to save draft.');
    } finally {
      setIsSavingDraft(false);
    }
  };

  // ── GPS auto-fill for vehicle location ──────────────────────────────────
  useEffect(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude: lat, longitude: lng } = pos.coords;
        setValue('pickupLat', lat);
        setValue('pickupLng', lng);
        try {
          const resp = await fetch(
            `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json`,
            { headers: { 'Accept-Language': 'en' } }
          );
          const json = await resp.json();
          const addr = json.display_name ?? `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
          setValue('locationAddress', addr, { shouldValidate: true });
        } catch {
          setValue('locationAddress', `${lat.toFixed(5)}, ${lng.toFixed(5)}`);
        }
      },
      () => {},
      { enableHighAccuracy: true, timeout: 8_000 }
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!isAuthenticated) return null;

  const cardBase     = 'flex-1 rounded-xl border-2 p-4 cursor-pointer transition-all text-left';
  const cardInactive = 'border-gray-200 bg-white hover:border-gray-300';

  return (
    <div className="page-shell">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Post a Job</h1>
        <p className="mt-1 text-sm text-gray-500">Free to post. Choose the job type below.</p>
      </div>

      {/* ── Job type selector ────────────────────────────────────────── */}
      <JobTypeSelector active="repair" />

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">

        {/* ── 1. Urgency ───────────────────────────────────────────── */}
        <div>
          <p className="text-sm font-semibold text-gray-700 mb-3">How urgent is this?</p>
          <div className="flex gap-3">
            <button type="button"
              className={`${cardBase} ${urgencyType === 'urgent' ? 'border-red-500 bg-red-50' : cardInactive}`}
              onClick={() => setValue('urgencyType', 'urgent')}>
              <div className="flex items-center gap-2 font-semibold text-gray-900 mb-1">
                <Zap className="w-4 h-4 text-red-500" /> Urgent
              </div>
              <p className="text-xs text-gray-500">Breakdown or needs repair ASAP.</p>
            </button>
            <button type="button"
              className={`${cardBase} ${urgencyType === 'scheduled' ? 'border-blue-500 bg-blue-50' : cardInactive}`}
              onClick={() => setValue('urgencyType', 'scheduled')}>
              <div className="flex items-center gap-2 font-semibold text-gray-900 mb-1">
                <Calendar className="w-4 h-4 text-blue-500" /> Scheduled
              </div>
              <p className="text-xs text-gray-500">Book for a specific date &amp; time.</p>
            </button>
            <button type="button"
              className={`${cardBase} ${urgencyType === 'flexible' ? 'border-green-500 bg-green-50' : cardInactive}`}
              onClick={() => setValue('urgencyType', 'flexible')}>
              <div className="flex items-center gap-2 font-semibold text-gray-900 mb-1">
                <Clock className="w-4 h-4 text-green-600" /> Flexible
              </div>
              <p className="text-xs text-gray-500">Timing agreed with the workshop.</p>
            </button>
          </div>
        </div>

        {urgencyType === 'urgent' && (
          <div className="rounded-xl border border-red-200 bg-red-50 p-4">
            <p className="text-xs font-semibold text-red-700 uppercase tracking-wide mb-3">Needed by</p>
            <input type="datetime-local" {...register('urgentDeadline')}
              className="w-full border border-red-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-red-400" />
          </div>
        )}

        {urgencyType === 'scheduled' && (
          <div className="rounded-xl border border-blue-200 bg-blue-50 p-4">
            <p className="text-xs font-semibold text-blue-700 uppercase tracking-wide mb-3">Preferred date &amp; time</p>
            <input type="datetime-local" {...register('scheduledAt')}
              className="w-full border border-blue-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500" />
          </div>
        )}

        {urgencyType === 'flexible' && (
          <div className="rounded-lg bg-green-50 border border-green-100 px-4 py-3 text-sm text-green-700">
            Timing will be agreed with the workshop during bidding.
          </div>
        )}

        {/* ── 2. Vehicle photos / videos ───────────────────────────── */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            <Camera className="inline w-3 h-3 mr-1" />
            Vehicle Photos &amp; Videos{' '}
            <span className="text-gray-400 font-normal">(optional — up to {MAX_FILES} files, helps AI describe the issue)</span>
          </label>

          {vehiclePreviews.length > 0 && (
            <div className="grid grid-cols-4 gap-2 mb-2">
              {vehiclePreviews.map((src, i) => (
                <div key={i} className="relative rounded-lg overflow-hidden border border-gray-200 bg-gray-50" style={{ aspectRatio: '1' }}>
                  {vehicleFiles[i]?.type.startsWith('video/') ? (
                    <video src={src} className="w-full h-full object-cover" muted playsInline />
                  ) : (
                    <img src={src} alt={`Vehicle ${i + 1}`} className="w-full h-full object-cover" />
                  )}
                  <button type="button" onClick={() => removeFile(i)}
                    className="absolute top-1 right-1 bg-black/60 hover:bg-black/80 text-white rounded-full p-0.5 transition-colors">
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {vehiclePreviews.length < MAX_FILES && (
            <div
              onDragOver={handleDragOver} onDragEnter={handleDragOver}
              onDragLeave={handleDragLeave} onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-colors ${
                isDragOver ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-blue-400'
              }`}
            >
              <Camera className="w-7 h-7 text-gray-400 mx-auto mb-1.5" />
              <p className="text-sm text-gray-500">
                {vehiclePreviews.length === 0
                  ? 'Click or drag & drop vehicle photos / videos here'
                  : `Add more (${vehiclePreviews.length}/${MAX_FILES} added)`}
              </p>
              <p className="text-xs text-gray-400 mt-0.5">JPG, PNG, WebP, MP4, MOV — up to 15 MB each</p>
            </div>
          )}

          <input ref={fileInputRef} type="file" accept={ACCEPT} multiple className="hidden" onChange={handleFileChange} />
        </div>

        {/* ── 3. Vehicle registration ──────────────────────────────── */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Vehicle Registration</label>
          <input
            {...register('vehicleRegistration')}
            placeholder="e.g. AB12 CDE"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 uppercase"
          />
          {errors.vehicleRegistration && (
            <p className="mt-1 text-xs text-red-600">{errors.vehicleRegistration.message}</p>
          )}
        </div>

        {/* ── 3b. Service type ─────────────────────────────────────── */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            Service / Repair Type <span className="text-gray-400 font-normal">(optional — helps match the right workshop)</span>
          </label>
          <div className="flex flex-wrap gap-2">
            {REPAIR_SERVICE_TYPES.map(st => {
              const isSelected = selectedServiceTypes.includes(st.value);
              return (
                <button
                  key={st.value}
                  type="button"
                  onClick={() =>
                    setSelectedServiceTypes(prev =>
                      prev.includes(st.value) ? prev.filter(v => v !== st.value) : [...prev, st.value]
                    )
                  }
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                    isSelected
                      ? 'bg-orange-500 text-white border-orange-500'
                      : 'bg-white text-gray-600 border-gray-300 hover:border-orange-400'
                  }`}
                >
                  {isSelected ? '✓ ' : ''}{st.label}
                </button>
              );
            })}
          </div>
          {descValue.trim() && (
            <button
              type="button"
              onClick={handleAiDetectServiceType}
              disabled={aiServiceTyping}
              className="mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border border-violet-400 bg-violet-50 text-violet-700 hover:bg-violet-100 text-xs font-semibold transition-colors"
            >
              {aiServiceTyping
                ? <span className="w-3 h-3 border-2 border-violet-400 border-t-transparent rounded-full animate-spin" />
                : <Sparkles className="w-3 h-3" />
              }
              AI Detect from Description
            </button>
          )}
        </div>

        {/* ── 4. Issue description with AI ─────────────────────────── */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label className="text-sm font-medium text-gray-700">Issue Description</label>
            <div className="flex gap-2">
              {!descValue.trim() && hasImage && (
                <button type="button" onClick={handleAiGenerateDescription} disabled={aiDescGenerating}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border border-violet-400 bg-violet-50 text-violet-700 hover:bg-violet-100 text-xs font-semibold transition-colors">
                  {aiDescGenerating
                    ? <span className="w-3 h-3 border-2 border-violet-400 border-t-transparent rounded-full animate-spin" />
                    : <Sparkles className="w-3 h-3" />
                  }
                  AI Generate
                </button>
              )}
              {descValue.trim() && (
                <button type="button" onClick={handleAiImproveDescription} disabled={aiDescImproving}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border border-emerald-400 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 text-xs font-semibold transition-colors">
                  {aiDescImproving
                    ? <span className="w-3 h-3 border-2 border-emerald-400 border-t-transparent rounded-full animate-spin" />
                    : <Sparkles className="w-3 h-3" />
                  }
                  AI Improve
                </button>
              )}
            </div>
          </div>
          <textarea
            {...register('issueDescription')}
            rows={4}
            placeholder="Describe the issue — e.g. engine warning light on, won't start, strange noise when braking..."
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
          />
          {errors.issueDescription && (
            <p className="mt-1 text-xs text-red-600">{errors.issueDescription.message}</p>
          )}
        </div>

        {/* ── 5. Vehicle location with postcode autocomplete ───────── */}
        <PostcodeField
          label="Vehicle Location"
          iconEl={<MapPin className="inline w-3 h-3 mr-1 text-blue-500" />}
          value={locationAddress}
          onChange={v => setValue('locationAddress', v, { shouldValidate: true })}
          onGeocode={(lat, lng) => {
            setValue('pickupLat', lat);
            setValue('pickupLng', lng);
          }}
          error={errors.locationAddress?.message}
        />

        {/* ── 6. Bidding type ─────────────────────────────────────── */}
        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <p className="text-sm font-semibold text-gray-700">Bidding Type</p>
              <p className="text-xs text-gray-500 mt-0.5">
                {isClosedBidding
                  ? 'Closed — workshop quote prices are hidden from each other. Only you see all quotes.'
                  : 'Open — workshops can see each other\'s quote prices.'}
              </p>
            </div>
            <button type="button" role="switch" aria-checked={isClosedBidding}
              onClick={() => setIsClosedBidding(v => !v)}
              className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-violet-500 focus:ring-offset-2 ${
                isClosedBidding ? 'bg-violet-600' : 'bg-gray-200'
              }`}
            >
              <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                isClosedBidding ? 'translate-x-5' : 'translate-x-0'
              }`} />
            </button>
          </div>
          {isClosedBidding && (
            <div className="mt-3 rounded-lg bg-violet-50 border border-violet-200 px-3 py-2 text-xs text-violet-700">
              Workshops will only see their own quote price. You see all quotes to select the best.
            </div>
          )}
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">{error}</div>
        )}

        <div className="bg-orange-50 border border-orange-100 rounded-lg p-4 text-xs text-orange-700">
          <strong>Free to post.</strong> Workshops bid on your repair — you pick the best price.
          {repairCommissionPercent != null ? ` A ${repairCommissionPercent}% platform fee is charged to the workshop, not you.` : ' Platform fee is charged to the workshop, not you.'}
        </div>

        <div style={{ display: 'flex', gap: 10 }}>
          <button
            type="button"
            onClick={handleSubmit(onSaveRepairDraft)}
            disabled={isSavingDraft || isSubmitting}
            className="flex-1 bg-gray-100 hover:bg-gray-200 disabled:bg-gray-100 text-gray-700 font-medium py-3 px-6 rounded-lg transition-colors border border-gray-300"
          >
            {isSavingDraft ? 'Saving...' : 'Save as Draft'}
          </button>
          <button
            type="submit"
            disabled={isSubmitting || isSavingDraft}
            className="flex-1 bg-orange-500 hover:bg-orange-600 disabled:bg-orange-300 text-white font-medium py-3 px-6 rounded-lg transition-colors"
          >
            {isSubmitting ? 'Posting...' : 'Post Repair Job — Free'}
          </button>
        </div>
      </form>
    </div>
  );
}

// ─────────────────────────────────────────────
//  EARNINGS DASHBOARD PAGE
// ─────────────────────────────────────────────
export function EarningsDashboardPage() {
  const { getToken } = useAppAuth();
  const [dashboard, setDashboard] = useState<{
    totalEarnings: number;
    referralEarnings: number;
    cashbackWallet: number;
    completedJobs: number;
    recentPayments: Array<{
      id: string;
      jobId: string;
      jobReference: string;
      grossAmount: number;
      commissionDeducted: number;
      netAmount: number;
      status: number;
      releasedAt: string | null;
    }>;
  } | null>(null);

  const [loading, setLoading] = useState(true);
  const [feeConfig, setFeeConfig] = useState<{ deliveryJobCommissionPercent: number; repairJobCommissionPercent: number } | null>(null);

  useEffect(() => {
    createApiClient(getToken)
      .get('/api/earnings/dashboard')
      .then(r => { setDashboard(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [getToken]);

  useEffect(() => {
    platformFeeConfigService(createApiClient(getToken)).getEffective()
      .then(setFeeConfig)
      .catch(() => {});
  }, [getToken]);

  const commissionNote = feeConfig
    ? feeConfig.deliveryJobCommissionPercent === feeConfig.repairJobCommissionPercent
      ? `the ${feeConfig.deliveryJobCommissionPercent}% platform commission`
      : 'the platform commission'
    : 'the platform commission';

  const statusLabel = (s: number) => ['Pending', 'Processing', 'Released', 'Failed'][s] ?? 'Unknown';
  const statusColour = (s: number) =>
    s === 2 ? 'text-green-600 bg-green-50' : s === 3 ? 'text-red-600 bg-red-50' : 'text-amber-600 bg-amber-50';

  if (loading) return (
    <div className="page-shell flex justify-center">
      <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full" />
    </div>
  );

  return (
    <div className="page-shell">
      <h1 className="text-2xl font-bold text-gray-900 mb-8">Earnings Dashboard</h1>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        {[
          { label: 'Total Earned', value: `£${(dashboard?.totalEarnings ?? 0).toFixed(2)}`, colour: 'text-green-600' },
          { label: 'Referral Income', value: `£${(dashboard?.referralEarnings ?? 0).toFixed(2)}`, colour: 'text-blue-600' },
          { label: 'Cashback Wallet', value: `£${(dashboard?.cashbackWallet ?? 0).toFixed(2)}`, colour: 'text-purple-600' },
          { label: 'Jobs Completed', value: String(dashboard?.completedJobs ?? 0), colour: 'text-gray-900' },
        ].map(card => (
          <div key={card.label} className="bg-white border border-gray-200 rounded-xl p-4">
            <p className="text-xs text-gray-500 mb-1">{card.label}</p>
            <p className={`text-2xl font-bold ${card.colour}`}>{card.value}</p>
          </div>
        ))}
      </div>

      {/* Payment Timeline */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100">
          <h2 className="font-semibold text-gray-900">Recent Payments</h2>
          <p className="text-xs text-gray-500 mt-0.5">24–48 hours after POD verification</p>
        </div>
        <div className="divide-y divide-gray-50">
          {(dashboard?.recentPayments ?? []).map(p => (
            <div key={p.id} className="px-6 py-4 flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-900">Job {p.jobReference}</p>
                <p className="text-xs text-gray-500 mt-0.5">
                  Gross £{p.grossAmount.toFixed(2)} — Commission £{p.commissionDeducted.toFixed(2)}
                </p>
                {p.releasedAt && (
                  <p className="text-xs text-gray-400">{new Date(p.releasedAt).toLocaleDateString('en-GB')}</p>
                )}
              </div>
              <div className="text-right">
                <p className="text-lg font-bold text-green-600">£{p.netAmount.toFixed(2)}</p>
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusColour(p.status)}`}>
                  {statusLabel(p.status)}
                </span>
              </div>
            </div>
          ))}
          {(dashboard?.recentPayments ?? []).length === 0 && (
            <div className="px-6 py-12 text-center text-gray-400 text-sm">
              No payments yet. Complete your first job to see earnings here.
            </div>
          )}
        </div>
      </div>

      {/* Payment info */}
      <div className="mt-4 bg-blue-50 border border-blue-100 rounded-lg p-4 text-xs text-blue-700">
        <strong>Payment timeline:</strong> Payments are released after the operations team
        verifies your proof of delivery and customer signature. No other deductions beyond {commissionNote}.
      </div>
    </div>
  );
}
